import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type Scenario } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';

const SCENARIOS = 'scenarios';

const converter = buildConverter<Scenario>(
  (data) => data as Scenario,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreScenarioRepository {
  async findAll(): Promise<Scenario[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, SCENARIOS), where('environment', '==', getCurrentEnvironment())).withConverter(
      converter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<Scenario | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, SCENARIOS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<Scenario[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SCENARIOS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(scenario: Scenario): Promise<Scenario> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(scenario.id);
    const data = { ...scenario, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, SCENARIOS, scenario.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, SCENARIOS, scenario.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async updateStatus(id: string, status: Scenario['status']): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await updateDoc(doc(db, SCENARIOS, id), { status, updatedAt: serverTimestamp() });
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, SCENARIOS, id));
  }
}

export const createFirestoreScenarioRepository = (): FirestoreScenarioRepository => new FirestoreScenarioRepository();
